import { ArrowBack, Call, MailOutline, WhatsApp } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, Typography } from '@mui/material';
import { lazy, Suspense, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Channels } from '@bandido/shared/constants';
import { CaseResponseDto } from '@bandido/shared/dtos';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useWhatsappClient } from '@frontend/features/whatsapp/components/WhatsappClientContext';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { useMobileViewport } from '@frontend/features/common/hooks/useMobileViewport';
import { removeLocalStorageValue } from '@frontend/features/common/hooks/useLocalStorage';
import { DraftProvider } from '../context/DraftContext';
import { useEditor } from '../hooks/useEditor';
import { NoteEditorComposeState } from './NoteEditor/types';
import {
  getLastMobileReply,
  getMobileCompose,
  mobileComposeKey,
  subscribeMobileCompose,
  updateMobileCompose,
} from '../mobileCaseComposeStore';

const NoteEditor = lazy(() => import('./NoteEditor').then((module) => ({ default: module.NoteEditor })));

export const CaseMobileCommunications = ({ caseData }: { caseData?: CaseResponseDto }) => {
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const { openWhatsapp } = useWhatsappClient();
  const { handleOpenOutgoingCallModal } = useTwilio();
  const editor = useEditor();
  const caseId = caseData?.id;
  const scope = `${myProfile?.id}:${caseId}`;
  const historyId = editor.historyIdToReply ?? null;
  const key = mobileComposeKey(scope, historyId);
  const draftKey = `case-mobile-email:${key}`;
  const subscribe = useCallback((listener: () => void) => subscribeMobileCompose(key, listener), [key]);
  const snapshot = useCallback(() => getMobileCompose(key), [key]);
  const entry = useSyncExternalStore(subscribe, snapshot, snapshot);
  const isOpen = editor.isOpen && editor.channel === Channels.EMAIL;
  const viewport = useMobileViewport(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (entry?.compose?.form.cc.length && !editor.showCc) editor.onToggleCc();
    if (entry?.compose?.form.bcc.length && !editor.showBcc) editor.onToggleBcc();
  }, [isOpen, entry?.compose?.form.cc.length, entry?.compose?.form.bcc.length,
    editor.showCc, editor.showBcc, editor.onToggleCc, editor.onToggleBcc]);

  const minimize = () => {
    editor.setIsOpen(false);
    editor.setChannel(null);
  };
  const wasOpen = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (!isOpen || !entry?.completed) return;
    if (justOpened) updateMobileCompose(scope, historyId, { pending: false });
    else minimize();
  }, [entry?.completed, isOpen, scope, historyId]);

  const openEmail = () => {
    const lastReply = getLastMobileReply(scope);
    const saved = getMobileCompose(mobileComposeKey(scope, lastReply));
    const state = saved?.completed ? undefined : saved?.compose;
    editor.setHistoryIdToReply(state?.historyId ?? null);
    editor.setTo(state?.form.to ?? []);
    editor.setCc(state?.form.cc ?? []);
    editor.setBcc(state?.form.bcc ?? []);
    editor.setFrom(state?.form.from ?? '');
    editor.setSubject(state?.form.subject ?? '');
    editor.setEditorContent('');
    if (Boolean(state?.form.cc.length) !== editor.showCc) editor.onToggleCc();
    if (Boolean(state?.form.bcc.length) !== editor.showBcc) editor.onToggleBcc();
    if (saved?.completed) updateMobileCompose(scope, null, { pending: false });
    editor.setChannel(Channels.EMAIL);
    editor.setIsOpen(true);
  };

  const preserve = useCallback(
    (compose: NoteEditorComposeState) => {
      const previous = getMobileCompose(key);
      if (previous?.completed) return;
      updateMobileCompose(scope, historyId, { compose, pending: previous?.pending ?? false }, true);
    },
    [key, scope, historyId],
  );
  const sendStarted = useCallback(() => {
    updateMobileCompose(scope, historyId, { compose: getMobileCompose(key)?.compose, pending: true });
  }, [key, scope, historyId]);
  const sendFailed = useCallback(() => {
    updateMobileCompose(scope, historyId, { compose: getMobileCompose(key)?.compose, pending: false });
  }, [key, scope, historyId]);
  const sent = useCallback(() => {
    removeLocalStorageValue(draftKey);
    updateMobileCompose(scope, historyId, { pending: false, completed: true });
  }, [draftKey, scope, historyId]);

  const recipient = caseData?.clientEmail
    ? [
      {
        id: caseData.clientId ?? caseData.clientEmail,
        email: caseData.clientEmail,
        firstName: caseData.clientFirstName ?? '',
        lastName: caseData.clientLastName ?? '',
      },
    ]
    : [];
  const normalize = (values: typeof editor.to = []) =>
    values.map((value) => ({
      ...value,
      firstName: value.firstName ?? '',
      lastName: value.lastName ?? '',
    }));
  const initial: NoteEditorComposeState = entry?.compose ?? {
    historyId,
    content: '',
    attachments: [],
    form: {
      to: historyId ? normalize(editor.to) : recipient,
      cc: normalize(editor.cc),
      bcc: normalize(editor.bcc),
      from: editor.from,
      subject: editor.subject,
      content: '',
    },
  };

  return (
    <>
      <Box
        display="flex"
        gap={1}
        sx={{
          '& > button': {
            flex: 1,
            minWidth: 0,
            px: 0.5,
            textTransform: 'none',
            minHeight: 44,
            fontSize: 12,
            '& .MuiButton-startIcon': { ml: 0, mr: 0.5 },
            '& svg': { fontSize: 18 },
          },
        }}
      >
        <Button
          variant="outlined"
          startIcon={<WhatsApp />}
          disabled={!caseData?.clientWhatsappNumber || !caseData?.clientId}
          onClick={() => {
            if (caseData?.clientId) openWhatsapp(caseData.clientId);
          }}
        >
          WhatsApp
        </Button>
        <Button variant="outlined" startIcon={<MailOutline />} disabled={!caseId || !myProfile?.id} onClick={openEmail}>
          {t('cases.view.contactEmail')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Call />}
          disabled={!caseId}
          onClick={() => {
            if (caseId && caseData) handleOpenOutgoingCallModal(caseId, caseData);
          }}
        >
          {t('cases.view.contactCall')}
        </Button>
      </Box>
      <Dialog
        fullScreen
        open={Boolean(isOpen && caseId)}
        onClose={minimize}
        aria-label={t('cases.view.contactEmail')}
        PaperProps={{
          sx: {
            position: 'fixed',
            ...viewport,
            maxHeight: 'none',
            minWidth: 0,
            pt: 'env(safe-area-inset-top)',
            pb: 'env(safe-area-inset-bottom)',
            '& input, & textarea, & .MuiSelect-select': { fontSize: 16 },
            '& .MuiIconButton-root, & .MuiButton-root, & .MuiToggleButton-root': { minHeight: 44, minWidth: 44 },
            '& .MuiToggleButtonGroup-root': { flexWrap: 'wrap' },
            '& form > .MuiToolbar-root': {
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} flexShrink={0}>
          <Button startIcon={<ArrowBack />} onClick={minimize}>
            {t('cases.view.backToCase')}
          </Button>
          <Typography variant="body2">#{caseId}</Typography>
        </Box>
        {isOpen && caseId && (
          <DraftProvider key={key} caseId={draftKey}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Suspense fallback={<CircularProgress sx={{ m: 3 }} />}>
                <NoteEditor
                  caseId={caseId}
                  initialComposeState={initial}
                  restoreComposeState={Boolean(entry?.compose)}
                  onComposeStateChange={preserve}
                  isSubmitBlocked={entry?.pending}
                  onSendStart={sendStarted}
                  onSendError={sendFailed}
                  onSent={sent}
                  closeAfterSent={false}
                  onMinimize={minimize}
                  onClose={minimize}
                  embedded
                  showFullscreenControl={false}
                />
              </Suspense>
            </Box>
          </DraftProvider>
        )}
      </Dialog>
    </>
  );
};
